<template>
  <div class="timeline-section">
    
    <!-- Config Verification Display -->
    <v-card v-if="config" class="pa-3 mb-3" variant="outlined" color="info">
      <div class="text-subtitle-2 font-weight-bold mb-2">
        Configuration Summary
      </div>
      <div class="config-display">
        <div v-if="config.type === 'single'">
          <v-chip size="small" color="primary">Single Date</v-chip>
          <span class="ml-2">{{ formatDate(config.singleDate) }}</span>
        </div>
        <div v-if="config.type === 'multiple'">
          <v-chip size="small" color="primary">Multiple Dates</v-chip>
          <div class="mt-2">
            <div v-if="config.dateRange">
              <strong>Date Range:</strong> 
              {{ formatDate(config.dateRange.start) }} - {{ formatDate(config.dateRange.end) }}
            </div>
            <div v-if="config.years">
              <strong>Years:</strong> {{ config.years.join(', ') }}
            </div>
            <div v-if="config.months">
              <strong>Months:</strong> {{ config.months.join(', ') }}
            </div>
            <div v-if="config.weekdays">
              <strong>Weekdays:</strong> {{ config.weekdays.join(', ') }}
            </div>
            <div v-if="config.times">
              <strong>Times:</strong> {{ config.times.join(', ') }}
            </div>
            <div v-if="config.toleranceHours">
              <strong>Tolerance:</strong> {{ formatTolerance(config.toleranceHours) }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Count of ranges in not allowed -->
      <div v-if="allowedDates" class="mt-3">
        <v-divider class="my-2" />
        <div class="text-subtitle-2 font-weight-bold mb-2">
          Allowed Dates Check
        </div>
        <v-chip 
          :color="countNotAllowedRange === 0 ? 'success' : 'error'" 
          size="small"
          class="mb-2"
        >
          {{ countNotAllowedRange === 0 ? '✓ All ranges within allowed dates' : `✗ ${countNotAllowedRange} ranges outside allowed dates` }}
        </v-chip>
      </div>
      
      
      <!-- Validation Results -->
      <div v-if="validationResults" class="mt-3">
        <v-divider class="my-2" />
        <div class="text-subtitle-2 font-weight-bold mb-2">
          Validation Results
        </div>
        <v-chip 
          :color="validationResults.isValid ? 'success' : 'error'" 
          size="small"
          class="mb-2"
        >
          {{ validationResults.isValid ? '✓ All ranges match config' : '✗ Validation failed' }}
        </v-chip>
        <div v-if="!validationResults.isValid" class="text-caption error-text">
          <div v-for="error in validationResults.errors" :key="error">
            • {{ error }}
          </div>
        </div>
        <div class="text-caption mt-1">
          Total ranges: {{ ranges.length }}
        </div>
      </div>
    </v-card>
    
    <!-- Timeline Ranges -->
    <v-card class="pa-3" variant="tonal">
      <div class="d-flex justify-space-between align-center mb-3">
        <span class="text-subtitle-2 font-weight-bold">
          Generated Time Ranges ({{ ranges.length }})
        </span>
      </div>

      <v-virtual-scroll
        :items="ranges"
        height="300"
        item-height="80"
      >
        <template v-slot:default="{ item, index }">
          <div class="range-item pa-2" :class="getRangeValidationClass(item, index)">
            <div class="d-flex justify-space-between">
              <span>
                {{ formatDateTime(item.start) }} - {{ formatDateTime(item.end) }}
              </span>
              <span class="text-caption">
                {{ formatDuration(item.end - item.start) }}
              </span>
            </div>
            <div v-if="getItemValidationError(item, index)" class="text-caption error-text mt-1">
              {{ getItemValidationError(item, index) }}
            </div>
          </div>
        </template>
      </v-virtual-scroll>
    </v-card>
  </div>
</template>

<script setup lang="ts">
// Generated by Copilot
import { computed } from 'vue';
import type { MillisecondRange } from '../types/datetime';
import type { TimeRangeConfig } from './date_time_range_generators';
import { validateRanges, DEFAULT_TOLERANCE, ALL_DAY_TOLERANCE } from './date_time_range_generators';

const props = defineProps<{
  ranges: MillisecondRange[];
  config?: TimeRangeConfig;
  allowedDates?: Date[];
}>();

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  badRanges: Set<MillisecondRange>;
}

const validationResults = computed<ValidationResult | null>(() => {
  if (!props.config) return null;
  
  const validation = validateRanges(props.ranges, props.config);
  
  return {
    isValid: validation.valid,
    errors: validation.errors,
    badRanges: new Set(validation.badRanges || [])
  };
});

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    timeZone: 'UTC'
  });
}

function formatDateTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    day: '2-digit',
    weekday: 'short',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC'
  });
}

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  return `${hours}h ${minutes}m`;
}

function formatTolerance(tolerance: [number, number]): string {
  const [before, after] = tolerance;
  
  // Check if it's all day tolerance
  if (before === ALL_DAY_TOLERANCE[0] && after === ALL_DAY_TOLERANCE[1]) {
    return 'All Day';
  }
  
  // Check if it's default tolerance
  if (before === DEFAULT_TOLERANCE[0] && after === DEFAULT_TOLERANCE[1]) {
    return `±${before}h (default)`;
  }
  
  // Check if it's symmetric
  if (before === after) {
    return `±${before}h`;
  }
  
  // Asymmetric tolerance
  return `-${before}h / +${after}h`;
}

const countNotAllowedRange = computed(() => {
  if (!props.allowedDates) return 0;
  
  const allowedDateSet = new Set(
    props.allowedDates.map(d => d.toISOString().split('T')[0])
  );
  
  let count = 0;
  
  for (const range of props.ranges) {
    const startDateStr = new Date(range.start).toISOString().split('T')[0];
    const endDateStr = new Date(range.end).toISOString().split('T')[0];
    
    if (!allowedDateSet.has(startDateStr) || !allowedDateSet.has(endDateStr)) {
      count += 1;
    }
  }
  
  return count;
});

function getRangeValidationClass(item: MillisecondRange, _index: number): string {
  if (!validationResults.value) return '';
  return validationResults.value.badRanges.has(item) ? 'validation-error' : 'validation-success';
}

function getItemValidationError(item: MillisecondRange, _index: number): string | undefined {
  if (!validationResults.value || !validationResults.value.badRanges.has(item)) return undefined;
  return 'This range does not match the configuration filters';
}
</script>

<style scoped>
.config-display {
  font-size: 0.875rem;
}

.range-item {
  border-left: 3px solid transparent;
  margin-bottom: 4px;
  border-radius: 4px;
}

.validation-success {
  border-left-color: rgb(var(--v-theme-success));
  background-color: rgba(var(--v-theme-success), 0.05);
}

.validation-error {
  border-left-color: rgb(var(--v-theme-error));
  background-color: rgba(var(--v-theme-error), 0.05);
}

.error-text {
  color: rgb(var(--v-theme-error));
}


</style>
